import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Send, Loader2, MessageSquare, ChevronLeft, XCircle, Building2 } from "lucide-react";
import { toast } from "sonner";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("id-ID", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

export default function MessagesView({ role }) {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [convs, setConvs] = useState([]);
  const [activeId, setActiveId] = useState(params.get("c") || null);
  const [messages, setMessages] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError] = useState(false);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [sending, setSending] = useState(false);
  const [text, setText] = useState("");
  const scrollRef = useRef();

  const active = convs.find((c) => c.id === activeId) || null;

  const loadList = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/conversations");
      setConvs(data || []);
      setListError(false);
    } catch { setListError(true); }
    finally { setLoadingList(false); }
  }, []);

  const loadMessages = useCallback(async (cid, markRead = true) => {
    if (!cid) return;
    setLoadingMsgs(true);
    try {
      const { data } = await apiClient.get(`/conversations/${cid}/messages`);
      setMessages(data || []);
      if (markRead) { apiClient.post(`/conversations/${cid}/read`).then(loadList).catch(() => {}); }
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally { setLoadingMsgs(false); }
  }, [loadList]);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { if (activeId) loadMessages(activeId); }, [activeId, loadMessages]);

  // light polling while a conversation is open
  useEffect(() => {
    if (!activeId) return;
    const t = setInterval(() => { loadMessages(activeId, false); loadList(); }, 5000);
    return () => clearInterval(t);
  }, [activeId, loadMessages, loadList]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const openConv = (id) => { setActiveId(id); setParams({ c: id }); };
  const backToList = () => { setActiveId(null); setParams({}); };

  const send = async () => {
    const t = text.trim();
    if (!t || sending || !activeId) return;
    setSending(true);
    try {
      await apiClient.post(`/conversations/${activeId}/messages`, { message: t });
      setText("");
      await loadMessages(activeId, false);
      await loadList();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally { setSending(false); }
  };

  const close = async () => {
    if (!activeId || !window.confirm("Tutup percakapan ini?")) return;
    try {
      await apiClient.post(`/conversations/${activeId}/close`);
      toast.success("Percakapan ditutup.");
      loadMessages(activeId, false); loadList();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };

  const otherName = (c) => (role === "customer" ? c.owner_name : c.customer_name) || "Pengguna";

  return (
    <div>
      <h1 className="font-heading text-3xl font-bold tracking-tight">Pesan</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        {role === "customer" ? "Percakapan dengan pemilik hunian." : "Percakapan dengan calon penyewa."}
      </p>

      <div className="mt-6 grid md:grid-cols-3 gap-4 h-[70vh]">
        {/* Conversation list */}
        <div className={`md:col-span-1 rounded-2xl border border-border bg-card overflow-hidden flex flex-col ${activeId ? "hidden md:flex" : "flex"}`}>
          <div className="px-4 py-3 border-b border-border font-heading font-semibold">Kotak Masuk</div>
          <div className="flex-1 overflow-y-auto" data-testid="conversation-list">
            {loadingList ? (
              <div className="py-10 grid place-items-center"><Loader2 className="animate-spin text-primary" size={22} /></div>
            ) : listError ? (
              <div className="p-6 text-center text-sm text-destructive" data-testid="conversations-error">
                Gagal memuat percakapan.
                <button onClick={loadList} className="ml-1 font-semibold text-primary hover:underline">Coba lagi</button>
              </div>
            ) : convs.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground" data-testid="conversations-empty">
                <MessageSquare size={24} className="mx-auto mb-2 text-primary" />
                Belum ada percakapan.
              </div>
            ) : (
              convs.map((c, i) => (
                <button key={c.id} onClick={() => openConv(c.id)} data-testid={`conversation-item-${i}`}
                  className={`w-full text-left px-4 py-3 border-b border-border hover:bg-secondary/50 transition-colors ${activeId === c.id ? "bg-secondary/60" : ""}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-sm truncate">{otherName(c)}</span>
                    <div className="flex items-center gap-1 shrink-0">
                      {c.status === "closed" && (
                        <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground" data-testid={`conversation-closed-${i}`}>Ditutup</span>
                      )}
                      {c.unread_count > 0 && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-accent text-accent-foreground" data-testid={`conversation-unread-${i}`}>
                          {c.unread_count}
                        </span>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-primary flex items-center gap-1 mt-0.5 truncate"><Building2 size={11} /> {c.property_name}</p>
                  <p className="text-xs text-muted-foreground truncate mt-0.5">{c.last_message_text || "Belum ada pesan"}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">{timeAgo(c.last_message_at)}</p>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Chat panel */}
        <div className={`md:col-span-2 rounded-2xl border border-border bg-card overflow-hidden flex flex-col ${activeId ? "flex" : "hidden md:flex"}`} data-testid="chat-panel">
          {!active ? (
            <div className="flex-1 grid place-items-center text-sm text-muted-foreground" data-testid="chat-empty">
              <div className="text-center"><MessageSquare size={28} className="mx-auto mb-2 text-primary" />Pilih percakapan untuk memulai.</div>
            </div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <button onClick={backToList} className="md:hidden p-1 -ml-1" data-testid="chat-back"><ChevronLeft size={20} /></button>
                  <div className="min-w-0">
                    <p className="font-heading font-semibold text-sm truncate" data-testid="chat-title">{otherName(active)}</p>
                    <p className="text-xs text-primary truncate">{active.property_name}</p>
                  </div>
                </div>
                {active.status === "active" ? (
                  <button onClick={close} data-testid="chat-close-button"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-destructive border border-border rounded-full px-3 py-1.5 hover:bg-destructive/10">
                    <XCircle size={13} /> Tutup
                  </button>
                ) : (
                  <span className="text-xs font-semibold text-muted-foreground" data-testid="chat-closed-badge">Ditutup</span>
                )}
              </div>

              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2 bg-secondary/20" data-testid="chat-messages">
                {loadingMsgs && messages.length === 0 ? (
                  <div className="py-10 grid place-items-center"><Loader2 className="animate-spin text-primary" size={22} /></div>
                ) : messages.length === 0 ? (
                  <div className="py-10 text-center text-sm text-muted-foreground">Belum ada pesan. Mulai percakapan.</div>
                ) : (
                  messages.map((m, i) => {
                    const mine = m.sender_id === user?.id;
                    return (
                      <div key={m.id} data-testid={`chat-message-${i}`} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 text-sm ${mine ? "bg-primary text-primary-foreground rounded-br-sm" : "bg-card border border-border rounded-bl-sm"}`}>
                          <p className="whitespace-pre-wrap break-words">{m.message}</p>
                          <p className={`text-[10px] mt-1 ${mine ? "text-primary-foreground/70" : "text-muted-foreground"}`}>{timeAgo(m.created_at)}</p>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {active.status === "active" ? (
                <div className="p-3 border-t border-border flex items-center gap-2">
                  <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
                    data-testid="chat-input" maxLength={2000} placeholder="Tulis pesan..."
                    className="flex-1 rounded-full border border-border bg-background px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
                  <button onClick={send} disabled={sending || !text.trim()} data-testid="chat-send-button"
                    className="grid place-items-center w-11 h-11 rounded-full bg-accent text-accent-foreground hover:scale-105 active:scale-95 transition-transform disabled:opacity-60 disabled:hover:scale-100">
                    {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                  </button>
                </div>
              ) : (
                <div className="p-3 border-t border-border text-center text-xs text-muted-foreground" data-testid="chat-closed-note">
                  Percakapan ditutup. Buat percakapan baru dari halaman properti bila diperlukan.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
